import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneCall, Wand2, Loader2, Trash2, Pencil, Star, Building2,
  MapPin, Mail, Phone, ExternalLink, Globe,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CopyButton } from "@/components/common/CopyButton";
import { useConfirm } from "@/components/common/Confirm";
import { useProspect } from "@/state/prospect";
import { useApi } from "@/hooks/useApi";
import { api } from "@/lib/api";
import { invalidate } from "@/lib/invalidate";
import { toast } from "@/components/ui/sonner";
import { money, dateShort, initials } from "@/lib/format";
import { gbpUrl } from "@/lib/gbp";
import { InfoTip, Hint } from "@/components/help/InfoTip";

type Account = {
  id: number; name: string; owner_name: string | null; phone: string | null; email: string | null;
  city: string | null; state: string | null; gbp_url: string | null; website: string | null; company: string | null; review_count: number | null;
  monthly_rev_cents: number | null; avg_ticket_cents: number | null; shop_type: string | null;
  status: string; notes: string | null;
  research_status: string | null; researched_at: string | null; research_json: string | null;
};
type Detail = { account: Account; bookings: any[]; calls: any[]; subscriptions: any[] };

export function ProspectDrawer() {
  const { id, closeProspect } = useProspect();
  const { data, reload } = useApi<Detail>(id ? `/api/accounts/${id}` : null);
  const account = data?.account;

  // poll while research is running; give up once it has clearly overrun so the
  // drawer flips to a retry state instead of polling a dead run forever.
  useEffect(() => {
    if (account?.research_status !== "pending") return;
    const startedAt = account?.researched_at ? Date.parse(account.researched_at) : 0;
    const t = setInterval(() => {
      reload();
      if (startedAt && Date.now() - startedAt > 115000) clearInterval(t);
    }, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account?.research_status, account?.researched_at]);

  return (
    <Sheet open={id != null} onOpenChange={(o) => !o && closeProspect()}>
      <SheetContent className="w-full overflow-y-auto scroll-slim p-0 sm:max-w-lg">
        {account ? <DrawerBody detail={data!} reload={reload} onClose={closeProspect} /> : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DrawerBody({ detail, reload, onClose }: { detail: Detail; reload: () => void; onClose: () => void }) {
  const account = detail.account;
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);

  const research = useMemo(() => {
    if (!account.research_json) return null;
    try { return JSON.parse(account.research_json); } catch { return null; }
  }, [account.research_json]);

  // A 'pending' that has run well past the backend's ~80s bound is effectively
  // dead (e.g. the request was dropped) — show a retry instead of spinning forever.
  const pendingStale =
    account.research_status === "pending" &&
    !!account.researched_at &&
    Date.now() - Date.parse(account.researched_at) > 110000;
  const researching = account.research_status === "pending" && !pendingStale;

  // Context-driven: opening a surface for this prospect sets the account
  // context via the URL — useAccountVars() on the destination primes every
  // script token from there. No manual "load into vars" step, no second
  // place account state lives.
  const openLiveCall = () => {
    onClose();
    navigate(`/accounts/${account.id}/live-call`);
  };
  const openAccount = () => {
    onClose();
    navigate(`/accounts/${account.id}/overview`);
  };

  const runResearch = (force = false) => {
    // The request runs the research synchronously (up to ~1 min). Fire it, then
    // pick up the 'pending' status so the spinner + 3s poll loop take over — the
    // briefing (or an error) lands as soon as the server writes it.
    toast("Researching the account…", { description: "Fetching their site + Google presence — up to a minute." });
    api
      .post(`/api/research/${account.id}${force ? "?force=1" : ""}`)
      .catch((e) => toast.error("Research unavailable", { description: e instanceof Error ? e.message : "" }))
      .finally(() => reload());
    window.setTimeout(reload, 600);
  };

  const del = async () => {
    let dep: any = null;
    try { dep = await api.get(`/api/accounts/${account.id}/dependents`); } catch { /* */ }
    const hasActive = (dep?.active_subs ?? 0) > 0;
    const parts = [
      dep?.bookings ? `${dep.bookings} booking(s)` : null,
      dep?.calls ? `${dep.calls} call(s)` : null,
      hasActive ? `an ACTIVE subscription worth ${money(dep.active_mrr_cents)}/mo` : (dep?.subs ? `${dep.subs} subscription(s)` : null),
    ].filter(Boolean);
    const ok = await confirm({
      title: `Delete ${account.name}?`,
      variant: "danger",
      message: parts.length ? <span>This permanently removes {parts.join(", ")}.</span> : "This can't be undone.",
      requireText: hasActive ? account.name : undefined,
      confirmText: hasActive ? "Delete everything" : "Delete",
    });
    if (!ok) return;
    try {
      await api.del(`/api/accounts/${account.id}${hasActive ? "?cascade=1" : ""}`);
      toast.success(`${account.name} deleted`);
      invalidate("account");
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Delete failed");
    }
  };

  return (
    <div className="flex flex-col">
      {/* Header */}
      <div className="border-b border-border p-5">
        <div className="flex items-start gap-3 pr-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-muted text-sm font-bold text-muted-foreground">
            {initials(account.name)}
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-lg font-bold">{account.name}</h2>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {account.city && <span className="flex items-center gap-1"><MapPin className="size-3" />{account.city}{account.state ? `, ${account.state}` : ""}</span>}
              <Hint tip="Funnel stage: lead, booked, showed, won, customer, churned or lost. Customer and Won get text and call tickets."><span><Badge variant="muted" className="capitalize">{account.status}</Badge></span></Hint>
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {account.phone && <a href={`tel:${account.phone}`} className="flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:border-primary/40"><Phone className="size-3" />{account.phone}</a>}
          {account.email && <a href={`mailto:${account.email}`} className="flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:border-primary/40"><Mail className="size-3" />email</a>}
          {account.website && <a href={account.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:border-primary/40"><Globe className="size-3" />{account.website.replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, "")}</a>}
          {gbpUrl(account) && <a href={gbpUrl(account)} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-md border border-border px-2 py-1 hover:border-primary/40"><ExternalLink className="size-3" />GBP</a>}
        </div>
      </div>

      {/* Primary actions — both set the account context by URL. */}
      <div className="grid grid-cols-2 gap-2 border-b border-border p-4">
        <Hint tip="Opens the Live Call cockpit for this prospect, ready for the sales call"><Button variant="cta" onClick={openLiveCall}><PhoneCall className="size-4" /> Live Call</Button></Hint>
        <Hint tip="Opens the full account page with all history"><Button variant="outline" onClick={openAccount}><Building2 className="size-4" /> Open account</Button></Hint>
      </div>

      <div className="space-y-4 p-5">
        {/* Key numbers */}
        <div className="grid grid-cols-3 gap-2">
          <Mini label="Revenue/mo" tip="Their business's monthly revenue" value={account.monthly_rev_cents != null ? money(account.monthly_rev_cents) : "—"} />
          <Mini label="Avg ticket" tip="Average dollar value of one of their jobs" value={account.avg_ticket_cents != null ? money(account.avg_ticket_cents) : "—"} />
          <Mini label="Reviews" value={account.review_count?.toString() ?? "—"} icon={Star} />
        </div>

        {/* AI Research */}
        <Card className="p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Wand2 className="size-3.5" /> AI Research <InfoTip>AI briefing built from the prospect's website, Google profile, reviews and map rank. AI-generated, so check it before relying on it.</InfoTip>
            </p>
            {researching ? (
              <Badge variant="warning" className="gap-1"><Loader2 className="size-3 animate-spin" /> researching</Badge>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => runResearch(!!research)} className="h-6 gap-1 px-2 text-xs">
                <Wand2 className="size-3" /> {research ? "Re-run" : pendingStale ? "Retry" : "Run research"}
              </Button>
            )}
          </div>
          {pendingStale && (
            <p className="text-sm text-destructive">Research took too long — retry above.</p>
          )}
          {research?.error && (
            <p className="text-sm text-destructive">Latest research run failed: {String(research.error)}</p>
          )}
          {research && (research.generated || research.gbp_summary) ? (
            <div className="space-y-2 text-sm">
              <p className="text-[11px] italic text-muted-foreground">{research.generated ?? "AI-generated — verify."}</p>
              {research.recommended_angle && <Field label="Angle" text={research.recommended_angle} />}
              {research.discovery_focus && <Field label="Discovery focus" text={research.discovery_focus} />}
              {research.gbp_summary && <Field label="Google profile" text={research.gbp_summary} />}
              {research.map_rank && <Field label="Map rank" text={research.map_rank} />}
              {Array.isArray(research.top_competitors) && research.top_competitors.length > 0 && (
                <Field label="Competitors" text={research.top_competitors.join(" · ")} />
              )}
              {research.missed_call_priors && <Field label="Missed-call priors" text={research.missed_call_priors} />}
            </div>
          ) : researching ? (
            <p className="text-sm text-muted-foreground">Fetching their website, profile, reviews and rank… up to a minute.</p>
          ) : !research && !pendingStale ? (
            <p className="text-sm text-muted-foreground">No research yet. Run it to get a cited briefing + the recommended angle before you dial.</p>
          ) : null}
        </Card>

        {/* Editable notes / edit form */}
        {editing ? (
          <EditForm account={account} onDone={() => { setEditing(false); reload(); invalidate("account"); }} />
        ) : (
          <>
            {account.notes && (
              <Card className="p-3 text-sm text-muted-foreground">{account.notes}</Card>
            )}
            {/* Bookings */}
            {detail.bookings.length > 0 && (
              <Section title={`Bookings (${detail.bookings.length})`}>
                {detail.bookings.map((b) => (
                  <Row key={b.id} left={dateShort(b.call_at)} mid={b.source} right={b.showed == null ? b.outcome : b.showed ? "showed" : "no-show"} />
                ))}
              </Section>
            )}
            {/* Calls */}
            {detail.calls.length > 0 && (
              <Section title={`Calls (${detail.calls.length})`}>
                {detail.calls.map((cl) => (
                  <Row key={cl.id} left={dateShort(cl.call_at)} mid={cl.outcome.replace("_", " ")} right={cl.cash_collected_cents > 0 ? money(cl.cash_collected_cents) : ""} />
                ))}
              </Section>
            )}
          </>
        )}
      </div>

      {/* Footer actions */}
      <div className="mt-auto flex items-center gap-2 border-t border-border p-4">
        <Hint tip="Edit this prospect's details and notes"><Button variant="outline" size="sm" onClick={() => setEditing((e) => !e)}><Pencil className="size-4" /> {editing ? "Done" : "Edit"}</Button></Hint>
        <Hint tip="Permanently deletes this account. You will be asked to confirm."><Button variant="ghost" size="sm" onClick={del} className="ml-auto text-destructive hover:bg-destructive/10"><Trash2 className="size-4" /> Delete</Button></Hint>
      </div>
    </div>
  );
}

function EditForm({ account, onDone }: { account: Account; onDone: () => void }) {
  const [f, setF] = useState({
    name: account.name, owner_name: account.owner_name ?? "", phone: account.phone ?? "", city: account.city ?? "",
    website: account.website ?? "", company: account.company ?? "",
    status: account.status,
    monthly_rev: account.monthly_rev_cents != null ? account.monthly_rev_cents / 100 : "",
    avg_ticket: account.avg_ticket_cents != null ? account.avg_ticket_cents / 100 : "",
    review_count: account.review_count ?? "",
    notes: account.notes ?? "",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/accounts/${account.id}`, {
        name: f.name, owner_name: f.owner_name || null, phone: f.phone || null, city: f.city || null,
        website: f.website || null, company: f.company || null,
        status: f.status,
        monthly_rev_cents: f.monthly_rev === "" ? null : Math.round(Number(f.monthly_rev) * 100),
        avg_ticket_cents: f.avg_ticket === "" ? null : Math.round(Number(f.avg_ticket) * 100),
        review_count: f.review_count === "" ? null : Number(f.review_count),
        notes: f.notes || null,
      });
      toast.success("Saved");
      onDone();
    } catch (e) { toast.error(e instanceof Error ? e.message : "Save failed"); }
    finally { setSaving(false); }
  };
  const set = (k: string, v: string) => setF((s) => ({ ...s, [k]: v }));
  return (
    <Card className="space-y-2 p-4">
      <Lab l="Name"><Input value={f.name} onChange={(e) => set("name", e.target.value)} className="h-8" /></Lab>
      <div className="grid grid-cols-2 gap-2">
        <Lab l="Business name"><Input value={f.company} onChange={(e) => set("company", e.target.value)} className="h-8" /></Lab>
        <Lab l="Website"><Input value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://…" className="h-8" /></Lab>
        <Lab l="Owner"><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)} className="h-8" /></Lab>
        <Lab l="Phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} className="h-8" /></Lab>
        <Lab l="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} className="h-8" /></Lab>
        <Lab l="Reviews"><Input type="number" value={f.review_count} onChange={(e) => set("review_count", e.target.value)} className="h-8" /></Lab>
        <Lab l="Rev/mo $"><Input type="number" value={f.monthly_rev} onChange={(e) => set("monthly_rev", e.target.value)} className="h-8" /></Lab>
        <Lab l="Ticket $"><Input type="number" value={f.avg_ticket} onChange={(e) => set("avg_ticket", e.target.value)} className="h-8" /></Lab>
      </div>
      <Lab l="Notes"><Textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} className="min-h-[60px]" /></Lab>
      <Button size="sm" onClick={save} disabled={saving} className="w-full">{saving ? "Saving…" : "Save"}</Button>
    </Card>
  );
}

const Lab = ({ l, children }: { l: string; children: React.ReactNode }) => (
  <label className="block"><span className="mb-1 block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{l}</span>{children}</label>
);
function Mini({ label, value, icon: Icon, tip }: { label: string; value: string; icon?: React.ComponentType<{ className?: string }>; tip?: string }) {
  return (
    <div className="rounded-lg border border-border p-2.5 text-center">
      <p className="tabular text-base font-bold">{value}</p>
      <p className="flex items-center justify-center gap-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{Icon && <Icon className="size-2.5" />}{label}{tip && <InfoTip>{tip}</InfoTip>}</p>
    </div>
  );
}
function Field({ label, text }: { label: string; text: string }) {
  return (
    <div className="group rounded-md bg-muted/40 p-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <CopyButton text={text} />
      </div>
      <p className="text-sm">{text}</p>
    </div>
  );
}
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</p>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ left, mid, right }: { left: string; mid: string; right: string }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-border px-2.5 py-1.5 text-sm">
      <span className="text-muted-foreground">{left}</span>
      <span className="capitalize">{mid}</span>
      <span className="tabular text-xs font-medium text-cta">{right}</span>
    </div>
  );
}
